import { addDays, addWeeks, format, getDaysInMonth } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import {
  MONTHL_TYPE,
  RECURRING_FREQUENCY,
} from '../constants/constants';

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_COMPACT = ['S', 'M', 'T', 'W', 'Th', 'F', 'S'];
const DATE_FORMAT = 'MMM d, yyyy';
const WEEKDAYS = [1, 2, 3, 4, 5];

const getWeekStart = (date) => addDays(date, -date.getDay());

const getDifferenceBetweenWeekdays = (currentDayIndex, nextDayIndex) => {
  if (currentDayIndex === nextDayIndex) return 0;
  const result = currentDayIndex - nextDayIndex;
  return result > 0 ? 7 - result : Math.abs(result);
};

const calculateWeekIndex = (weekIndex, total) => {
  if (weekIndex >= 0 && weekIndex < 3) return weekIndex;
  if (weekIndex >= 0 && weekIndex >= 3) return total - 1;
  if (weekIndex < 0) return total + weekIndex;
  return 0;
};

const getNthWeekdayOfMonth = (date, weekDay, weekIndex) => {
  const weekDays = [];
  const nextDate = new Date(date.getTime());
  nextDate.setDate(1);
  nextDate.setDate(nextDate.getDate() + ((7 + weekDay - nextDate.getDay()) % 7));

  while (nextDate.getMonth() === date.getMonth()) {
    weekDays.push(new Date(nextDate.getTime()));
    nextDate.setDate(nextDate.getDate() + 7);
  }

  return weekDays[calculateWeekIndex(weekIndex, weekDays.length)];
};

const addMonthsSafe = (date, months) => {
  const n = date.getDate();
  const result = new Date(date.getTime());
  result.setDate(1);
  result.setMonth(date.getMonth() + months);
  result.setDate(Math.min(n, getDaysInMonth(result)));
  return result;
};

const formatZonedDate = (datetime, timezone) => {
  if (!datetime || !timezone) return '';
  const zoned = utcToZonedTime(new Date(datetime), timezone);
  return format(zoned, DATE_FORMAT);
};

const ordinal = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const formatByDaysList = (byDays, { shortWeekdayLabels = false } = {}) => {
  const labels = shortWeekdayLabels ? DAY_COMPACT : DAY_SHORT;
  return [...byDays]
    .sort((a, b) => a - b)
    .map((d) => labels[d])
    .join(', ');
};

const formatRepeats = (rule, zonedStartDate, { shortWeekdayLabels = false } = {}) => {
  const { frequency, isCustom, interval, byDays, monthlyType } = rule || {};
  if (!frequency) return '';

  if (frequency === RECURRING_FREQUENCY.WEEKDAYS) {
    return shortWeekdayLabels ? 'Every weekday (M–F)' : 'Every weekday (Mon–Fri)';
  }

  if (frequency === RECURRING_FREQUENCY.DAILY && !isCustom) {
    return shortWeekdayLabels ? 'Every Day (M - S)' : 'Every Day (Mon - Sun)';
  }

  if (frequency === RECURRING_FREQUENCY.WEEKLY && byDays?.length) {
    const days = formatByDaysList(byDays, { shortWeekdayLabels });
    if (isCustom && interval > 1) {
      return `Every ${interval} weeks on ${days}`;
    }
    return `Every week on ${days}`;
  }

  if (frequency === RECURRING_FREQUENCY.MONTHLY) {
    if (monthlyType === MONTHL_TYPE.BY_WEEK_INDEX && zonedStartDate) {
      return `Every month on the ${format(zonedStartDate, 'EEEE')}`;
    }
    if (zonedStartDate) {
      const day = zonedStartDate.getDate();
      if (day === 31) {
        return 'Every month on the last day';
      }
      return `Every month on the ${ordinal(day)}`;
    }
    return 'Every month';
  }

  if (isCustom && interval) {
    if (frequency === RECURRING_FREQUENCY.DAILY) {
      return interval === 1 ? 'Every day' : `Every ${interval} days`;
    }
    if (frequency === RECURRING_FREQUENCY.WEEKLY) {
      return interval === 1 ? 'Every week' : `Every ${interval} weeks`;
    }
    if (frequency === RECURRING_FREQUENCY.MONTHLY) {
      return interval === 1 ? 'Every month' : `Every ${interval} months`;
    }
  }

  return '';
};

const calcFirstRecurrenceWeekStart = (zonedStartDate, weekSchema) => {
  const hasDatesOnFirstWeek = weekSchema.some((day) => day >= zonedStartDate.getDay());
  let firstWeekStart = getWeekStart(zonedStartDate);
  if (!hasDatesOnFirstWeek) {
    firstWeekStart = addWeeks(firstWeekStart, 1);
  }
  return firstWeekStart;
};

const getLastOccurrenceDate = (rule, zonedStartDate) => {
  const count = Number(rule?.count);
  const interval = Number(rule?.interval) || 1;
  if (!zonedStartDate || !count || count < 1) return null;

  const frequency = rule.frequency;

  if (frequency === RECURRING_FREQUENCY.DAILY) {
    return addDays(zonedStartDate, (count - 1) * interval);
  }

  if (frequency === RECURRING_FREQUENCY.MONTHLY) {
    if (rule.monthlyType === MONTHL_TYPE.BY_WEEK_INDEX) {
      const byDay = rule.byDays?.length ? rule.byDays[0] : zonedStartDate.getDay();
      const weekIndex = rule.weekIndex ?? 0;
      const firstRecurrenceDay = getNthWeekdayOfMonth(zonedStartDate, byDay, weekIndex);
      const startDate =
        firstRecurrenceDay.getTime() >= zonedStartDate.getTime()
          ? zonedStartDate
          : addMonthsSafe(zonedStartDate, 1);

      const dates = [];
      for (let i = 0; i < count; i++) {
        const nextMonth = addMonthsSafe(startDate, i * interval);
        dates.push(getNthWeekdayOfMonth(nextMonth, byDay, weekIndex));
      }
      return dates[dates.length - 1] || null;
    }

    return addMonthsSafe(zonedStartDate, (count - 1) * interval);
  }

  let weekSchema;
  if (frequency === RECURRING_FREQUENCY.WEEKDAYS) {
    weekSchema = WEEKDAYS;
  } else if (frequency === RECURRING_FREQUENCY.WEEKLY) {
    weekSchema = rule.byDays?.length > 0 ? [...rule.byDays].sort((a, b) => a - b) : [zonedStartDate.getDay()];
  } else {
    return null;
  }

  const firstWeekStart = calcFirstRecurrenceWeekStart(zonedStartDate, weekSchema);
  const dates = [];
  let week = 0;

  while (dates.length < count) {
    const weekStart = addWeeks(firstWeekStart, week * interval);
    weekSchema.forEach((day) => {
      const daysToAdd = getDifferenceBetweenWeekdays(weekStart.getDay(), day);
      const nextDate = addDays(weekStart, daysToAdd);
      if (dates.length < count && nextDate.getTime() >= zonedStartDate.getTime()) {
        dates.push(nextDate);
      }
    });
    week += 1;
    if (week > count * 14) break;
  }

  return dates[dates.length - 1] || null;
};

/** True when all fields needed for a complete recurring summary are filled. */
export const isRecurringRideSummaryReady = (recurrenceRule) => {
  const rule = recurrenceRule || {};
  if (!rule.frequency || !rule.count) return false;

  if (rule.isCustom) {
    if (!rule.interval) return false;
    if (rule.frequency === RECURRING_FREQUENCY.WEEKLY && !(rule.byDays?.length > 0)) {
      return false;
    }
    if (rule.frequency === RECURRING_FREQUENCY.MONTHLY && !rule.monthlyType) {
      return false;
    }
    return true;
  }

  if (rule.frequency === RECURRING_FREQUENCY.WEEKLY) {
    return rule.byDays?.length > 0;
  }

  if (rule.frequency === RECURRING_FREQUENCY.MONTHLY) {
    return Boolean(rule.monthlyType);
  }

  return true;
};

export const buildRecurringRideSummary = (
  recurrenceRule,
  fallbackStartPoint,
  isRoundtrip = false,
  { shortWeekdayLabels = false } = {},
) => {
  const startPoint = recurrenceRule?.startPoint?.datetime
    ? recurrenceRule.startPoint
    : fallbackStartPoint;

  const zonedStart =
    startPoint?.datetime && startPoint?.timezone
      ? utcToZonedTime(new Date(startPoint.datetime), startPoint.timezone)
      : null;

  const startDate = formatZonedDate(startPoint?.datetime, startPoint?.timezone);
  const numberOfRides = recurrenceRule?.count
    ? isRoundtrip
      ? `${recurrenceRule.count} roundtrip rides`
      : `${recurrenceRule.count} rides`
    : '';
  const repeats = formatRepeats(recurrenceRule, zonedStart, { shortWeekdayLabels });

  let endDate = '';
  if (zonedStart && recurrenceRule?.frequency && recurrenceRule?.count) {
    const last = getLastOccurrenceDate(recurrenceRule, zonedStart);
    if (last) {
      endDate = format(last, DATE_FORMAT);
    }
  }

  return {
    startDate,
    endDate,
    repeats,
    numberOfRides,
  };
};
